import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Headphones, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

export default function Support() {
  const location = useLocation();
  const [approved, setApproved] = useState(false);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!session) return undefined;
    const timer = window.setTimeout(() => {
      setSession(null);
      setMessage("Support access expired. Start a new call whenever you’re ready.");
    }, Math.max(0, session.expires_at * 1000 - Date.now()));
    return () => window.clearTimeout(timer);
  }, [session]);

  async function start() {
    setBusy(true);
    setMessage("");
    try {
      const { data } = await api.post("/support/sessions", {
        consent: approved,
        page: location.state?.from || "/app",
      });
      setSession(data);
      setMessage("Your secure support panel is opening below. Allow microphone access there to place the call.");
    } catch (error) {
      setMessage(error.response?.data?.detail || error.message || "Could not start support. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    try {
      await api.delete(`/support/sessions/${encodeURIComponent(session.id)}`);
      setSession(null);
      setMessage("Support access ended and revoked.");
    } catch {
      setMessage("Could not revoke access. Please retry.");
    }
  }

  const supportUrl = session
    ? `${session.portal}#${encodeURIComponent(session.token)}`
    : undefined;

  return (
    <section className="icaard-support-card max-w-xl mx-auto" data-testid="support-page">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#147D60]/10 text-[#147D60]">
          <Headphones size={23} />
        </div>
        <div>
          <h1 className="m-0 text-xl font-bold text-[#15353b]">Talk to a support agent</h1>
          <p className="mt-1 text-sm text-[#52686b]">Get help with your iCaard account without leaving the app.</p>
        </div>
      </div>

      <div className="my-4 rounded-xl border border-[#d7e7e4] bg-white/80 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#15353b]">
          <ShieldCheck size={17} className="text-[#08765b]" /> Your privacy is protected
        </div>
        <p className="mb-0 mt-2 text-sm leading-relaxed text-[#52686b]">
          If you approve, the agent can see your name, phone number, account role and the page you need help with. Access is read-only and expires after 30 minutes. Agents can’t see your balance or password, move money, or change your account.
        </p>
      </div>

      {!session ? (
        <>
          <label className="my-4 flex items-start gap-3 text-sm leading-relaxed text-[#36595a]">
            <input
              type="checkbox"
              checked={approved}
              onChange={(event) => setApproved(event.target.checked)}
              data-testid="support-consent"
              className="mt-1 h-4 w-4 accent-[#147D60]"
            />
            <span>I approve sharing these details for this support call.</span>
          </label>
          <button data-testid="support-start-call" disabled={!approved || busy} onClick={start}>
            {busy ? "Connecting…" : "Call support agent"}
          </button>
        </>
      ) : (
        <div className="mt-4" data-testid="support-inline-call">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#b8d9cc] bg-[#edf8f2] px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-[#15353b]">Secure support call</div>
              <div className="mt-0.5 text-xs text-[#52686b]">The call stays open inside iCaard.</div>
            </div>
            <button type="button" onClick={revoke} data-testid="support-end-call" className="!bg-white !text-[#8f2c3d] !border !border-[#e5c4ca]">
              End and revoke access
            </button>
          </div>
          <iframe
            key={session.id}
            src={supportUrl}
            title="iCaard secure support call"
            allow="microphone; autoplay"
            referrerPolicy="no-referrer"
            data-testid="support-call-frame"
            className="h-[70vh] min-h-[480px] max-h-[760px] w-full overflow-hidden rounded-2xl border border-[#d7e7e4] bg-white"
          />
        </div>
      )}

      {message && <p className="mt-3 text-sm text-[#52686b]" role="status" aria-live="polite">{message}</p>}
    </section>
  );
}
